import React, { useState, useRef } from 'react';
import get from 'lodash/get';
import classNames from 'classnames';
import { Form } from 'react-final-form';
import { FormApi } from 'final-form';
import {
  FieldProps,
  InjectedFieldProps,
  useDataProvider,
  useRefresh,
  useNotify,
} from 'react-admin';

import { Input } from 'components/form';
import SubmitButton from 'components/buttons/SubmitButton';
import { RewardLevelDto } from '../types';

import makeStyles from '@material-ui/core/styles/makeStyles';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 190,
    boxSizing: 'border-box',
    gap: '8px',
  },
  input: {
    width: 74,
    height: 44,
    boxSizing: 'border-box',
    padding: theme.spacing(1.5),
    textAlign: 'right',
  },
  button: {
    width: 60,
    height: 44,
    borderRadius: 8,
    textTransform: 'none',
    boxSizing: 'border-box',
    padding: 0,
    display: 'none',
    '&:hover': {
      display: 'flex',
    },
  },

  focused: {
    display: 'flex',
  },

  inputRoot: {
    marginTop: 0,
  },
}));

const onlyNumbers = (value: string) => value.replace(/[^0-9]*/g, '');

type EditableNumberFormValues = {
  editableValue?: number | string;
  newQty?: number | string;
  change?: boolean;
};

type EditableNumberFieldProps = Omit<FieldProps, 'source'> &
  InjectedFieldProps & {
    record?: RewardLevelDto;
    source: keyof RewardLevelDto;
  };

const EditableNumberField = ({ record, source }: EditableNumberFieldProps) => {
  const classes = useStyles();
  const isEditable = record?.validUntil === null;

  const notify = useNotify();

  const dataProvider = useDataProvider();
  const refresh = useRefresh();

  const formRef = useRef<FormApi<EditableNumberFormValues>>();

  const [focused, setFocused] = useState<boolean>(false);

  const handleClickStopPropagation = (event: React.SyntheticEvent) => event.stopPropagation();

  const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    handleClickStopPropagation(event);
    if (!isEditable) {
      return;
    }
    setFocused(true);
  };

  const handleSubmitForm = async (values: EditableNumberFormValues) => {
    if (!isEditable) {
      return;
    }

    try {
      const finalValue = values?.change ? Number(values.newQty) : Number(values.editableValue ?? 0);

      const formData = new FormData();

      formData.set('name', record?.name?.trim() ?? '');
      formData.set('minPoints', String(record?.minPoints ?? '0'));
      formData.set('minShipments', String(record?.minShipments ?? '0'));
      formData.set('minSpend', String(record?.minSpend ?? '0'));

      formData.set(source, String(finalValue ?? 0));

      await dataProvider.update('rewardsLevel', {
        id: get(record, 'id'),
        data: formData,
      });

      formRef?.current?.change('editableValue', finalValue);
      // update form's initial value so blur-driven resets keep the saved number
      formRef?.current?.initialize({ editableValue: finalValue });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'An error occurred while updating rewards level';
      notify(errorMessage, 'error');
    } finally {
      refresh();
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setFocused(false);

      try {
        formRef.current?.reset();
      } catch (error) {
        console.log('error --->', error);
      }
    }, 200);
  };

  return (
    <Form<EditableNumberFormValues>
      onSubmit={handleSubmitForm}
      initialValues={{
        editableValue: get(record, source, 0) || 0,
      }}
      render={({ handleSubmit, submitting, form }) => {
        formRef.current = form;

        return (
          <form onSubmit={handleSubmit}>
            <div className={classes.root}>
              <Input
                name="editableValue"
                inputProps={{
                  variant: 'outlined',
                  InputProps: { classes: { input: classes.input } },
                  onFocus: handleInputFocus,
                  onBlur: handleInputBlur,
                  onClick: handleClickStopPropagation,
                }}
                classes={{ input: classes.inputRoot }}
                disabled={submitting || !isEditable}
                formatValue={onlyNumbers}
              />
              <SubmitButton
                className={classNames(classes.button, focused && classes.focused)}
                onClick={handleClickStopPropagation}
                label="Save"
                submitting={submitting}
                size="medium"
              />
            </div>
          </form>
        );
      }}
    />
  );
};

export default EditableNumberField;
